/**
 * Connect Trade replace (Alpaca PATCH) often returns a new order id and never
 * cancels the id the chart is still drawing. Track those ids for the session
 * so a later working snapshot cannot put the old line back.
 */

const retiredOrderIds = new Set();
/** Order ids whose cancel is the first half of a Connect Trade replace. */
const silentReplaceCancelIds = new Set();

export function suppressReplaceCancelNotice(id) {
  if (id == null || id === '') return;
  silentReplaceCancelIds.add(String(id));
}

export function releaseReplaceCancelNotice(id) {
  if (id == null || id === '') return;
  silentReplaceCancelIds.delete(String(id));
}

/** True once for a replace-driven cancel, so the stream does not toast "Order Cancelled". */
export function consumeReplaceCancelNotice(id) {
  if (id == null || id === '') return false;
  const key = String(id);
  if (!silentReplaceCancelIds.has(key)) return false;
  silentReplaceCancelIds.delete(key);
  return true;
}

const WORKING_STATUSES = new Set(['ACK', 'DON', 'DONE', 'OPN', 'PLA', 'WORKING']);

export function retireOrderId(id) {
  if (id == null || id === '') return;
  retiredOrderIds.add(String(id));
}

export function isRetiredOrderId(id) {
  return id != null && retiredOrderIds.has(String(id));
}

export function isWorkingOrderStatus(status) {
  return WORKING_STATUSES.has(String(status || '').trim().toUpperCase());
}

function cancelRecord(order, orderId) {
  return {
    ...(order || { OrderID: orderId }),
    OrderID: order?.OrderID || orderId,
    Status: 'CAN',
    ClosedDateTime: order?.ClosedDateTime || new Date().toISOString(),
  };
}

/** Keep a replaced working order off the chart if a later event still says it is open. */
export function guardRetiredOrder(order) {
  if (!order?.OrderID || !isRetiredOrderId(order.OrderID)) return order;
  if (!isWorkingOrderStatus(order.Status)) return order;
  return cancelRecord(order, order.OrderID);
}

const TERMINAL_STATUSES = new Set([
  'FLL', 'FILLED', 'CAN', 'CANCELLED', 'CANCELED', 'REPLACED', 'REJ', 'REJECTED', 'EXP', 'EXPIRED',
]);

/** Cancel statuses produced by a replace (cancel old id, then place the new one). */
const REPLACE_CANCEL_STATUSES = new Set(['CAN', 'CANCELLED', 'CANCELED', 'REPLACED']);

export function isReplaceCancelStatus(status) {
  return REPLACE_CANCEL_STATUSES.has(String(status || '').trim().toUpperCase());
}

/**
 * A filled or cancelled id must not come back as a working chart line.
 * A working row whose filled quantity already covers the order is the fill
 * the orders table shows while the chart copy is still OPN.
 */
export function sealTerminalOrder(order) {
  if (!order?.OrderID) return order;
  let next = guardRetiredOrder(order);
  const ordered = parseFloat(next.Quantity ?? next.Legs?.[0]?.QuantityOrdered);
  const filled = parseFloat(next.FilledQuantity ?? next.Legs?.[0]?.ExecQuantity);
  if (
    Number.isFinite(ordered) && ordered > 0 &&
    Number.isFinite(filled) && filled >= ordered &&
    isWorkingOrderStatus(next.Status)
  ) {
    next = { ...next, Status: 'FLL' };
  }
  const status = String(next.Status || '').trim().toUpperCase();
  if (TERMINAL_STATUSES.has(status)) retireOrderId(next.OrderID);
  return next;
}

/**
 * When this order replaced another, retire that id.
 * @returns {string|null} the id that should be marked cancelled
 */
export function retireLinkedOrder(order) {
  const replacedId = order?.ReplacedOrderID;
  if (replacedId == null || replacedId === '') return null;
  const id = String(replacedId);
  if (order?.OrderID != null && String(order.OrderID) === id) return null;
  retireOrderId(id);
  return id;
}

/**
 * Apply a successful order-update response.
 * Same id: merge the new price onto the existing row.
 * New id: cancel the dragged id and insert the replacement.
 */
export function applyOrderReplaceResult(prev, requestedId, result) {
  const list = Array.isArray(prev) ? prev : [];
  if (!result || typeof result !== 'object') return list;
  if (result.error || result.Error) return list;
  const byId = new Map(list.map((order) => [String(order.OrderID), order]));
  const nextId = result.OrderID != null ? String(result.OrderID) : '';
  const explicit = result.ReplacedOrderID != null ? String(result.ReplacedOrderID) : '';
  const requested = requestedId != null ? String(requestedId) : '';
  const replacedId = explicit || (nextId && requested && nextId !== requested ? requested : '');
  if (replacedId && replacedId !== nextId) {
    retireOrderId(replacedId);
    byId.set(replacedId, cancelRecord(byId.get(replacedId), replacedId));
  }
  if (nextId) {
    byId.set(nextId, sealTerminalOrder({ ...(byId.get(nextId) || {}), ...result }));
  }
  return Array.from(byId.values());
}

/** Fold one streamed/REST order into an id map, retiring whatever it replaced. */
export function upsertStreamOrder(byId, order) {
  if (!order?.OrderID) return order;
  const key = String(order.OrderID);
  const replacedId = retireLinkedOrder(order);
  if (replacedId) {
    byId.set(replacedId, cancelRecord(byId.get(replacedId), replacedId));
  }
  const merged = sealTerminalOrder({ ...(byId.get(key) || {}), ...order });
  byId.set(key, merged);
  return merged;
}
